import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import LoginWithCustomButton from './components/LoginWithCustomButton';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import EmployeeManagement from './pages/EmployeeManagement';
import OpexManagement from './pages/OpexManagement';
import PPEManagement from './pages/PPEManagement';
import Schedule from './pages/Schedule';
import AttendanceManagement from './pages/AttendanceManagement';
import EvaluationPersonal from './pages/EvaluationPersonal';
import CRManagement from './pages/CRManagement';
import AttendanceAdmin from './pages/AttendanceAdmin';
import ScheduleAdmin from './pages/ScheduleAdmin';
import HRCostManagement from './pages/HRCostManagement';
import CashFlow from './pages/CashFlow';
import PermissionManagement from './pages/PermissionManagement';
import NotificationSettings from './pages/NotificationSettings';
import NotificationHistory from './pages/NotificationHistory';
import PayrollManagement from './pages/PayrollManagement';
import CorporateCardManagement from './pages/CorporateCardManagement';
import CorporateCardUpload from './pages/CorporateCardUpload';
import PrivacyPolicy from './pages/PrivacyPolicy';
import WorkSites from './pages/WorkSites';
import PptDraft from './pages/PptDraft';
import Dashboard from './pages/Dashboard';
import { MGMT_OPEN } from './services/dashboardApi';
import Sidebar from './components/Sidebar';

/** 첫 화면 — 경영현황이 열려 있으면 관리자는 /dashboard, 그 밖은 /schedule */
const Home: React.FC = () => {
  const { userRole } = useAuth();
  const admin = userRole === 'ADMIN' || userRole === 'DEVELOPER';
  return <Navigate to={MGMT_OPEN && admin ? '/dashboard' : '/schedule'} replace />;
};

/** 페이지(pathname)를 옮기면 맨 위에서 시작 — 스크롤은 window 가 하고 BrowserRouter 는 되돌리지 않는다. 쿼리 · 해시만 바뀔 때는 그대로 둔다 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * 템플릿 작성은 한 번 열면 다른 메뉴로 가도 닫지 않고 숨긴다 — AI 초안(3~5분) · 고치던 글이 그대로 남는다.
 * 새로고침 · 탭 닫기 때는 사라진다(원고 · 그림이 브라우저에만 있다).
 */
const KeepTemplate: React.FC = () => {
  const { pathname } = useLocation();
  const here = pathname === '/ppt-draft';
  const [opened, setOpened] = useState(here);
  useEffect(() => { if (here) setOpened(true); }, [here]);
  return opened ? <div hidden={!here}><PptDraft /></div> : null;
};

function App() {

  return (
    <AlertProvider>
      <AuthProvider>
          <Router>
          <Routes>
          <Route path="/login" element={<LoginWithCustomButton />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <div className="App flex min-h-screen bg-slate-100">
                  <Sidebar />
                  <div className="flex-1 overflow-x-auto">
                    <ScrollToTop />
                    <Routes>
                      {/* 첫 화면 — 관리자(ADMIN · DEVELOPER)는 경영현황(#97), 직원은 내 일정 */}
                      <Route path="/" element={<Home />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      
                      {/* 개인 영역 - 모든 권한 */}
                      <Route path="/schedule" element={
                        <ProtectedRoute>
                          <Schedule />
                        </ProtectedRoute>
                      } />
                      <Route path="/attendance" element={
                        <ProtectedRoute>
                          <AttendanceManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/evaluation-personal" element={
                        <ProtectedRoute>
                          <EvaluationPersonal />
                        </ProtectedRoute>
                      } />

                      {/* 관리자 전용 페이지 */}
                      <Route path="/employees" element={
                        <ProtectedRoute>
                          <EmployeeManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/attendance-admin" element={
                        <ProtectedRoute>
                          <AttendanceAdmin />
                        </ProtectedRoute>
                      } />
                      <Route path="/opex" element={
                        <ProtectedRoute>
                          <OpexManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/hr-cost" element={
                        <ProtectedRoute>
                          <HRCostManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/cash-flow" element={
                        <ProtectedRoute>
                          <CashFlow />
                        </ProtectedRoute>
                      } />

                      {/* 프로젝트 매니저 추가 접근 페이지 */}
                      <Route path="/schedule-admin" element={
                        <ProtectedRoute>
                          <ScheduleAdmin />
                        </ProtectedRoute>
                      } />
                      <Route path="/ppe" element={
                        <ProtectedRoute>
                          <PPEManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/cr" element={
                        <ProtectedRoute>
                          <CRManagement />
                        </ProtectedRoute>
                      } />

                      {/* 업무 사이트 › 사이트별 현황 — 권한은 페이지 안 ProtectedRoute(allow) 로. page_permissions 에 없음 */}
                      {/* 경영현황이 열리면 연결 앱 카드가 그 안에 있다 — 전체 현황은 그리로 */}
                      <Route path="/work-sites" element={MGMT_OPEN ? <Navigate to="/dashboard" replace /> : <WorkSites />} />
                      {/* 10-21 ~ 10-24 사이트별 페이지 주소 — 전체 현황으로 */}
                      <Route path="/work-sites/:target" element={<Navigate to={MGMT_OPEN ? '/dashboard' : '/work-sites'} replace />} />
                      {/* 업무 사이트 › 회사서류 (GRK_admin#101) — 권한은 페이지 안 ProtectedRoute(allow) */}
                      {/* 문서 · PPT (GRK_admin#113) — 권한은 페이지 안 ProtectedRoute(allow), 업무 사이트와 같다 */}
                      {/* 그리는 것은 아래 KeepTemplate — 여기는 주소만 받는다 */}
                      <Route path="/ppt-draft" element={null} />
                      {/* 10-27 「작성 가이드」는 템플릿 작성의 탭으로 합쳤다 · 10-28 「md 관리」로 이름을 바꿨다 */}
                      <Route path="/ppt-guides" element={<Navigate to="/ppt-draft#md" replace />} />

                      {/* 시스템 관리 페이지 */}
                      <Route path="/permission" element={
                        <ProtectedRoute>
                          <PermissionManagement />
                        </ProtectedRoute>
                      } />

                      {/* 알림 관리 페이지 */}
                      <Route path="/notification-settings" element={
                        <ProtectedRoute>
                          <NotificationSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/notification-history" element={
                        <ProtectedRoute>
                          <NotificationHistory />
                        </ProtectedRoute>
                      } />

                      {/* 급여대장 관리 */}
                      <Route path="/payroll" element={
                        <ProtectedRoute>
                          <PayrollManagement />
                        </ProtectedRoute>
                      } />

                      {/* 법인카드 관리 */}
                      <Route path="/corporate-card" element={
                        <ProtectedRoute>
                          <CorporateCardManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/corporate-card-upload" element={
                        <ProtectedRoute>
                          <CorporateCardUpload />
                        </ProtectedRoute>
                      } />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <KeepTemplate />
                  </div>
                </div>
              </PrivateRoute>
            }
          />
            </Routes>
          </Router>
      </AuthProvider>
    </AlertProvider>
  );
}

export default App;
